import React, { useMemo } from "react";

import "@local-logic/maps/style.css";

import { LocalLogicMap } from "@local-logic/maps";

import { Layers } from "./Layers";
import { Markers } from "./Markers";
import { Popup } from "./Popup";

import * as styles from "./styles";
import { getMapProviderConfig } from "~/utils/map/getMapProviderConfig";
import {
  DEFAULT_MIN_ZOOM,
  DEFAULT_MAX_ZOOM,
  MAP_PADDING,
} from "~/sdks/NeighborhoodMap/constants";
import { useResponsiveBreakpoints } from "~/hooks/useResponsiveBreakpoints";
import { useNeighborhoodMap } from "~/sdks/NeighborhoodMap/context";

export function Map({
  children,
  ...rest
}: {
  children?: React.ReactNode;
} & React.HTMLAttributes<HTMLDivElement>) {
  const breakpoints = useResponsiveBreakpoints();
  const { locale, map, activeMarker, geography } = useNeighborhoodMap();

  const mapProviderConfig = useMemo(
    () =>
      getMapProviderConfig(
        map.mapProvider?.name,
        map.mapProvider?.key,
        map.mapProvider?.theme,
      ),
    [map.mapProvider?.name, map.mapProvider?.key, map.mapProvider?.theme],
  );

  const locationBoundary = useMemo(() => {
    if (typeof geography === "undefined" || !geography?.geometry) {
      return undefined;
    }

    const outerRing = geography.geometry.flatMap((polygon) => polygon[0] || []);

    if (!outerRing || outerRing.length === 0) {
      return undefined;
    }
    return outerRing.map((point: number[]) => [point[1], point[0]]);
  }, [geography]);

  return (
    <div className={styles.container} {...rest}>
      {typeof geography !== "undefined" && (
        <LocalLogicMap.Root
          locale={locale}
          mapProvider={mapProviderConfig}
          mapPadding={MAP_PADDING[breakpoints.sm ? "mobile" : "desktop"]}
          locationBoundary={locationBoundary}
          center={{
            latitude: geography?.centroid.lat,
            longitude: geography?.centroid.lng,
          }}
          zoom={map.zoom}
          minZoom={DEFAULT_MIN_ZOOM}
          maxZoom={DEFAULT_MAX_ZOOM}
          zoomPosition={breakpoints.sm ? "bottom-right" : undefined}
          pitch={map.pitch}
          bearing={map.bearing}
          cooperativeGestures={map.cooperativeGestures}
          activeMarker={activeMarker}
        >
          <LocalLogicMap.BaseMap>
            <Layers />
            <Markers />
            <Popup />
          </LocalLogicMap.BaseMap>
        </LocalLogicMap.Root>
      )}

      {children}
    </div>
  );
}
